import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Divider,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Alert,
  Snackbar,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  ContactPhone as ContactPhoneIcon,
  LocalHospital as MedicalIcon,
  Gavel as ConsentIcon,
  Payments as PaymentsIcon,
  Notes as NotesIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import axios from '../utils/axios';
import CampusMinistryForm from './CampusMinistryForm';
import {
  STUDENT_CATEGORY_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  formatCurrency as fmtMoney,
  formatDateOnly,
  totalPaid as sumPaid,
  balanceDue,
  fullName
} from '../utils/campusMinistryFields';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

const Field = ({ label, value, full }) => (
  <Grid item xs={12} sm={full ? 12 : 6}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{value || '—'}</Typography>
  </Grid>
);

const SectionCard = ({ icon, title, children, action }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="h6">{title}</Typography>
      </Box>
      {action}
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const CampusMinistryDetails = ({ registration, open, onClose, onUpdate }) => {
  const [reg, setReg] = useState(registration);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [txn, setTxn] = useState({ amount: '', method: 'cash', reference: '' });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    setReg(registration);
  }, [registration]);

  if (!reg) return null;

  const paid = sumPaid(reg);
  const balance = balanceDue(reg);
  const contacts = reg.emergencyContacts || [];
  const medications = (reg.medications || []).filter(m => m.name || m.prescribingPhysician || m.phone);

  const refresh = (data) => {
    if (data) setReg(data);
    if (onUpdate) onUpdate();
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await axios.post(`/campus-ministry/${reg._id}/notes`, { content: noteText.trim() });
      setNoteText('');
      setSuccess('Note added');
      refresh(res.data);
    } catch (e) {
      setError('Failed to add note');
    }
  };

  const handleAddTransaction = async () => {
    const amount = parseFloat(txn.amount);
    if (!amount || amount <= 0) {
      setError('Enter a valid transaction amount');
      return;
    }
    try {
      const res = await axios.post(`/campus-ministry/${reg._id}/payment/transaction`, {
        amount,
        method: txn.method,
        reference: txn.reference
      });
      setTxn({ amount: '', method: 'cash', reference: '' });
      setSuccess('Payment recorded');
      refresh(res.data);
    } catch (e) {
      setError('Failed to record payment');
    }
  };

  const handleToggleHardship = async () => {
    try {
      const newStatus = reg.payment?.status === 'hardship' ? 'open' : 'hardship';
      const res = await axios.patch(`/campus-ministry/${reg._id}/payment`, { status: newStatus });
      setSuccess(newStatus === 'hardship' ? 'Marked as hardship' : 'Hardship removed');
      refresh(res.data);
    } catch (e) {
      setError('Failed to update payment status');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/campus-ministry/${reg._id}`);
      setConfirmDelete(false);
      if (onUpdate) onUpdate();
      onClose();
    } catch (e) {
      setError('Failed to delete registration');
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" component="span">{fullName(reg)}</Typography>
            <Chip
              size="small"
              sx={{ ml: 2, fontWeight: 600 }}
              label={PAYMENT_STATUS_LABELS[reg.payment?.status] || 'Balance Due'}
              color={PAYMENT_STATUS_COLORS[reg.payment?.status] || 'warning'}
            />
            <Typography variant="body2" color="text.secondary">{reg.eventName}</Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <SectionCard icon={<PersonIcon color="primary" />} title="Participant">
            <Grid container spacing={2}>
              <Field label="First Name" value={reg.firstName} />
              <Field label="Last Name" value={reg.lastName} />
              <Field label="Date of Birth" value={formatDateOnly(reg.dateOfBirth)} />
              <Field label="Email" value={reg.email} />
              <Field label="Student Category" value={STUDENT_CATEGORY_LABELS[reg.studentCategory]} />
              <Field label="Roommate Choice(s)" value={(reg.roommateChoices || []).filter(Boolean).join(', ')} />
              <Field label="Registered" value={fmtDate(reg.registrationDate)} />
              <Field label="Source" value={reg.source === 'manual' ? 'Entered by staff' : 'Online form'} />
            </Grid>
          </SectionCard>

          <SectionCard icon={<ContactPhoneIcon color="primary" />} title="Emergency Contacts">
            {contacts.length === 0 && <Typography variant="body2" color="text.secondary">None provided.</Typography>}
            {contacts.map((c, i) => (
              <Box key={i} sx={{ mb: i < contacts.length - 1 ? 2 : 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Emergency Contact {i + 1}</Typography>
                <Grid container spacing={2}>
                  <Field label="Name" value={c.name} />
                  <Field label="Address" value={c.address} />
                  <Field label="Phone (day)" value={c.phoneDay} />
                  <Field label="Phone (evening)" value={c.phoneEvening} />
                </Grid>
              </Box>
            ))}
          </SectionCard>

          <SectionCard icon={<MedicalIcon color="primary" />} title="Medical & Insurance">
            <Grid container spacing={2}>
              <Field label="Primary Doctor" value={reg.primaryDoctor?.name} />
              <Field label="Doctor Phone" value={reg.primaryDoctor?.phone} />
            </Grid>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Medications</Typography>
            {medications.length === 0 ? (
              <Typography variant="body2" color="text.secondary">None listed.</Typography>
            ) : (
              <TableContainer sx={{ mb: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Medication</TableCell>
                      <TableCell>Prescribing Physician</TableCell>
                      <TableCell>Phone</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {medications.map((m, i) => (
                      <TableRow key={i}>
                        <TableCell>{m.name || '—'}</TableCell>
                        <TableCell>{m.prescribingPhysician || '—'}</TableCell>
                        <TableCell>{m.phone || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Field label="Medical Conditions" value={reg.medicalConditions} full />
              <Field label="Allergies — To medicines" value={reg.allergies?.medicines} />
              <Field label="Allergies — Other" value={reg.allergies?.other} />
              <Field label="Insurance Carrier" value={reg.insurance?.carrier} />
              <Field label="Plan" value={reg.insurance?.plan} />
              <Field label="Policy #" value={reg.insurance?.policyNumber} />
            </Grid>
          </SectionCard>

          <SectionCard icon={<ConsentIcon color="primary" />} title="Consent & Signature">
            <Grid container spacing={2}>
              <Field label="Consent to Medical Treatment, Release and Indemnity" value={reg.consent?.agreed ? `Agreed${reg.consent.agreedAt ? ` on ${fmtDate(reg.consent.agreedAt)}` : ''}` : 'Not recorded'} />
              <Field label="Signed By" value={reg.signature?.name} />
              <Field label="Signature Date" value={fmtDate(reg.signature?.date)} />
            </Grid>
            {reg.consent?.textSnapshot && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Text agreed to</Typography>
                <Box sx={{ maxHeight: 160, overflowY: 'auto', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{reg.consent.textSnapshot}</Typography>
                </Box>
              </Box>
            )}
          </SectionCard>

          <SectionCard
            icon={<PaymentsIcon color="primary" />}
            title="Payment"
            action={
              <Button size="small" variant="outlined" onClick={handleToggleHardship}>
                {reg.payment?.status === 'hardship' ? 'Remove Hardship' : 'Mark Hardship'}
              </Button>
            }
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Field label="Total Due" value={fmtMoney(reg.payment?.amount)} />
              <Field label="Total Paid" value={fmtMoney(paid)} />
              <Field label="Balance" value={fmtMoney(balance)} />
              <Field label="Method Chosen" value={reg.payment?.method === 'online' ? 'Online' : 'In person'} />
            </Grid>

            {(reg.payment?.transactions || []).length > 0 && (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Reference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reg.payment.transactions.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell>{fmtDate(t.date)}</TableCell>
                        <TableCell>{fmtMoney(t.amount)}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{t.method}</TableCell>
                        <TableCell>{t.reference || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Record a Payment</Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={txn.amount}
                  onChange={(e) => setTxn({ ...txn, amount: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  label="Method"
                  size="small"
                  fullWidth
                  value={txn.method}
                  onChange={(e) => setTxn({ ...txn, method: e.target.value })}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="sga">SGA / Sponsor</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={8} sm={4}>
                <TextField
                  label="Reference (optional)"
                  size="small"
                  fullWidth
                  value={txn.reference}
                  onChange={(e) => setTxn({ ...txn, reference: e.target.value })}
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <Button variant="contained" fullWidth onClick={handleAddTransaction}>Add</Button>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard icon={<NotesIcon color="primary" />} title="Notes">
            <Stack spacing={1} sx={{ mb: 2 }}>
              {(reg.notes || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No notes yet.</Typography>
              )}
              {(reg.notes || []).map((n, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2">{n.content}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {n.author?.name} · {fmtDate(n.date)}
                  </Typography>
                </Paper>
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add a note"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <Button variant="contained" onClick={handleAddNote}>Add</Button>
            </Stack>
          </SectionCard>

          <SectionCard icon={<HistoryIcon color="primary" />} title="Activity Log">
            <Stack spacing={1}>
              {(reg.activity || []).slice().reverse().map((a, i) => (
                <Box key={i}>
                  <Typography variant="body2">
                    <strong>{a.action}</strong>{a.details ? ` — ${a.details}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.user?.name || 'System'} · {fmtDate(a.date)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </SectionCard>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
          <Box>
            <Button onClick={onClose} sx={{ mr: 1 }}>Close</Button>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <CampusMinistryForm
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSuccess={(updated) => { setEditOpen(false); refresh(updated); }}
        registration={reg}
      />

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete Registration?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete {fullName(reg)}'s {reg.eventName} registration. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </>
  );
};

export default CampusMinistryDetails;
